import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  useGetCurrentPlanQuery,
  useGetSeatStatusQuery,
  useGetTokenUsageQuery,
} from "@/services/(company)/subscriptionApi";
import { PATHS } from "@/routes/paths";

import {
  buildPlanIdentity,
  buildPlanResources,
  ringResource,
} from "./plan-hub.data";
import type { IPlanIdentity, IPlanResource, UsageStatus } from "./types";

interface PlanHubState {
  resources: IPlanResource[];
  plan: IPlanIdentity;
  /** Ring fill + color for the pill and the panel hero (worst resource wins). */
  ringPercentage: number;
  ringStatus: UsageStatus;
  isLoading: boolean;
  hasSeats: boolean;
  goToSeats: () => void;
  goToTokens: () => void;
  goToBilling: () => void;
}

/**
 * Owns the plan hub's data: seat status, token usage and the current plan.
 *
 * Callers gate the mount (admin + subscription + access), so nothing is
 * skipped here. All three queries are arg-less, sharing the header's RTK
 * cache keys — mounting alongside `useSubscriptionSummary` re-uses the
 * in-flight `getTokenUsage` request rather than issuing a duplicate.
 */
export function usePlanHub(): PlanHubState {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "company"]);

  const { data: seatData, isLoading: seatsLoading } = useGetSeatStatusQuery();
  const { data: tokenData, isLoading: tokensLoading } = useGetTokenUsageQuery();
  const { data: planData } = useGetCurrentPlanQuery();

  const resources = useMemo(
    () =>
      buildPlanResources({
        t,
        seats: seatData?.data,
        tokens: tokenData?.data,
      }),
    [seatData?.data, tokenData?.data, t],
  );

  const plan = useMemo(
    () => buildPlanIdentity(planData?.data, t),
    [planData?.data, t],
  );

  const ring = ringResource(resources);

  return {
    resources,
    plan,
    ringPercentage: ring?.percentage ?? 0,
    ringStatus: ring?.status ?? "healthy",
    isLoading: seatsLoading || tokensLoading,
    hasSeats: resources.some((r) => r.key === "seats"),
    goToSeats: () =>
      navigate(PATHS.COMPANY.COMPANY_SUBSCRIPTION_PATHS.VTAB_MANAGEMENT_SEATS),
    goToTokens: () => navigate(PATHS.STANDALONE_PATHS.SUBSCRIPTION_TOKENS),
    goToBilling: () =>
      navigate(PATHS.COMPANY.COMPANY_SUBSCRIPTION_PATHS.DASHBOARD),
  };
}
